/**
 * Pure selectors over the merged UBP-Q4 context. Three groups:
 *   A. wizard flow (ported from the flow source)
 *   B. per-product reads (ported from the calc source)
 *   C. selection selectors that bridge flow ↔ products
 *
 * Selectors return only data (keys, numbers, enums) — never display strings.
 * Labels and number/currency formatting live in components/strings.ts.
 * Composition is done by calling other selectors with the same `ctx`.
 */
import { calculateIncludedAllowance } from '../calculator/allowances';
import { getProductPricingConfig } from '../calculator/product-pricing-configs';
import { QueryValue } from '../util/url';

import { STEP_ORDER } from './constants';
import { validateOrgUserCount } from './validation';

import type { UbpContextValue } from './context';
import type { BillingCycle, Meter, Product, ProductCategory, Step, UsageType } from './types';
import type { OrgUserCountError } from './validation';
import type { Edition, ProductKey } from '../calculator/types';

export type Selector<T> = (ctx: UbpContextValue) => T;

// --- A. flow selectors ---

export const selectStep: Selector<Step> = (ctx) => ctx.step;
export const selectMeter: Selector<Meter> = (ctx) => ctx.meter;
export const selectUsageType: Selector<UsageType> = (ctx) => ctx.usageType;
export const selectUsage: Selector<number> = (ctx) => ctx.usage;
export const selectOrgUserCount: Selector<number | undefined> = (ctx) => ctx.orgUserCount;
export const selectOrgUserCountError: Selector<OrgUserCountError | undefined> = (ctx) =>
	ctx.orgUserCountError;

export const selectCanAdvanceUsageStep: Selector<boolean> = (ctx) =>
	validateOrgUserCount(selectOrgUserCount(ctx)) === undefined;

export const selectCanAdvanceAllowanceStep: Selector<boolean> = (ctx) =>
	selectSelectedProducts(ctx).length > 0;

export const selectStepIndex: Selector<number> = (ctx) => STEP_ORDER.indexOf(selectStep(ctx));

export const selectIncludedAllowance: Selector<number> = (ctx) =>
	calculateIncludedAllowance({ products: ctx.products, meter: ctx.meter });

export interface UsageRange {
	low: number;
	high: number;
}

export const selectEstimatedUsageRange: Selector<UsageRange> = () => {
	// TODO(GWP-12277): derive from orgUserCount + meter + usageType.
	return { low: 145000, high: 160000 };
};

// --- B. per-product reads ---

export const getRequiredProduct = (ctx: UbpContextValue, productKey: ProductKey): Product => {
	const product = ctx.products.find((p) => p.productKey === productKey);
	if (!product) {
		throw new Error(`No product found for productKey="${productKey}"`);
	}
	return product;
};

export const getProductEdition = (ctx: UbpContextValue, productKey: ProductKey): Edition =>
	getRequiredProduct(ctx, productKey).inputs.edition;

export const getProductUnitCount = (ctx: UbpContextValue, productKey: ProductKey): number =>
	getRequiredProduct(ctx, productKey).inputs.unitCount;

export const getProductBillingCycle = (
	ctx: UbpContextValue,
	productKey: ProductKey,
): BillingCycle => getRequiredProduct(ctx, productKey).inputs.billingCycle;

export const getProductPerUnitPerMonth = (
	ctx: UbpContextValue,
	productKey: ProductKey,
): number | undefined => ctx.calculation.byProductKey[productKey];

export const isCcpDataPending = (ctx: UbpContextValue): boolean =>
	ctx.ccpStatus === 'idle' || ctx.ccpStatus === 'fetching';

export const getCurrencyIsoCode = (ctx: UbpContextValue) => ctx.currencyISOCode;

export const getNumberFormatLocale = (ctx: UbpContextValue) => ctx.localeISOCode;

// --- C. selection selectors (flow ↔ products bridge) ---

export const selectSelectedProducts: Selector<Product[]> = (ctx) => ctx.products;

export const selectIsProductSelected =
	(key: ProductKey): Selector<boolean> =>
	(ctx) =>
		ctx.products.some((p) => p.productKey === key);

export const selectProductTabSelectionWarningShown: Selector<boolean> = (ctx) =>
	ctx.productTabSelectionWarningShown;

export const selectCategoryHasSelections =
	(category: ProductCategory): Selector<boolean> =>
	(ctx) =>
		ctx.products.some((p) => getProductPricingConfig(p.productKey).category === category);

export const selectSelectedCategory: Selector<ProductCategory | null> = (ctx) => {
	const first = ctx.products[0];
	return first ? getProductPricingConfig(first.productKey).category : null;
};

export const selectInitialProductsTab: Selector<ProductCategory> = (ctx) =>
	selectSelectedCategory(ctx) ?? 'app';

// --- URL serialization selector (inverse of initialValuesFromQuery) ---

export const selectShareableQuery: Selector<Record<string, QueryValue>> = (ctx) => {
	const products = selectSelectedProducts(ctx);
	const query: Record<string, QueryValue> = {
		step: selectStep(ctx),
		meter: selectMeter(ctx),
		usageType: selectUsageType(ctx),
		usage: selectUsage(ctx),
		orgUserCount: selectOrgUserCount(ctx),
		products: products.map((p) => p.productKey),
	};
	for (const p of products) {
		query[`product.${p.productKey}`] = {
			unitCount: p.inputs.unitCount,
			edition: p.inputs.edition,
			billingCycle: p.inputs.billingCycle,
		};
	}
	return query;
};
